// Handler factory for transaction status management — add, rename, delete,
// bulk-move transactions, reorder, and minimize statuses. D-07 (39-15): the
// manual-archive handler has been removed entirely -- archive is now
// system-driven only, via archiveYear (src/utils/archiveGrouping.js), so no
// status-level "archived" toggle exists to write here.

import * as workspaceService from '../services/workspaceService';
import * as transactionService from '../services/transactionService';
import { sanitizeStatusNameList } from './useWorkspaceData';
import { isDefaultStatus, UNCATEGORIZED_STATUS } from '../config/statuses';

export function useStatusHandlers({
    selectedWorkspaceId,
    selectedWorkspace,
    run,
    showToast,
    statuses,
    statusOrder,
    customStatuses,
    persistStatusOrder,
    persistStatusMetadata,
    applyLocalStatusOrder,
}) {
    const handleAddStatus = async (newStatus) => {
        const trimmed = (newStatus || '').trim();
        if (!trimmed || !selectedWorkspaceId) return;
        const normalized = trimmed.toLowerCase();
        const existingNames = statuses.map(status => (status || '').toLowerCase());
        if (existingNames.includes(normalized)) {
            showToast("Status already exists.", "error");
            return;
        }
        await run(async () => {
            await workspaceService.createStatus(selectedWorkspaceId, trimmed);
            showToast("Status added!");
        }, "Failed to add status.");
    };

    const handleRenameStatus = async (statusId, nextName) => {
        if (!selectedWorkspaceId || !statusId) return;
        const trimmed = (nextName || '').trim();
        if (!trimmed) {
            showToast("Enter a status name.", "error");
            return;
        }
        const target = customStatuses.find(status => status.id === statusId);
        if (!target) return;
        const oldName = (target.name || '').trim();
        const normalizedOld = oldName.toLowerCase();
        const normalizedNew = trimmed.toLowerCase();
        if (normalizedOld === normalizedNew) return;
        const duplicate = statuses
            .filter(status => (status || '').toLowerCase() !== normalizedOld)
            .some(status => (status || '').toLowerCase() === normalizedNew);
        if (duplicate) {
            showToast("A status with that name already exists.", "error");
            return;
        }
        await run(async () => {
            await workspaceService.renameStatus(selectedWorkspaceId, statusId, trimmed);
            await workspaceService.renameStatusOnTransactions(selectedWorkspaceId, oldName, trimmed);
            const minimizedStatuses = sanitizeStatusNameList(
                (selectedWorkspace?.minimizedStatuses || []).map(name => (
                    (name || '').trim().toLowerCase() === normalizedOld ? trimmed : name
                )),
                statusOrder.map(name => ((name || '').trim().toLowerCase() === normalizedOld ? trimmed : name))
            );
            const nextStatusOrder = statusOrder.map(name => (
                (name || '').trim().toLowerCase() === normalizedOld ? trimmed : name
            ));
            await persistStatusMetadata({
                statusOrder: nextStatusOrder,
                minimizedStatuses
            });
            applyLocalStatusOrder(nextStatusOrder);
            showToast("Status updated!");
        }, "Failed to update status.");
    };

    const handleDeleteStatus = async (statusId) => {
        if (!selectedWorkspaceId || !statusId) return;
        const target = customStatuses.find(status => status.id === statusId);
        if (!target) return;
        const statusName = (target.name || '').trim();
        if (!statusName || isDefaultStatus(statusName)) {
            showToast("Cannot delete this status.", "error");
            return;
        }
        await run(async () => {
            await workspaceService.deleteStatus(selectedWorkspaceId, statusId);
            await workspaceService.renameStatusOnTransactions(selectedWorkspaceId, statusName, UNCATEGORIZED_STATUS);
            const remainingStatuses = statusOrder.filter(name => name !== statusName);
            await persistStatusMetadata({
                statusOrder: remainingStatuses,
                minimizedStatuses: sanitizeStatusNameList(selectedWorkspace?.minimizedStatuses || [], remainingStatuses)
            });
            applyLocalStatusOrder(remainingStatuses);
            showToast("Status deleted.");
        }, "Failed to delete status.");
    };

    const handleBulkMoveTransactions = async (transactionIds = [], targetStatus = '') => {
        if (!selectedWorkspaceId) return;
        if (!Array.isArray(transactionIds) || transactionIds.length === 0) return;
        const trimmedStatus = (targetStatus || '').trim();
        if (!trimmedStatus) return;
        if (!statuses.some(status => status.toLowerCase() === trimmedStatus.toLowerCase())) {
            showToast("Select a valid status.", "error");
            return;
        }
        await run(async () => {
            await transactionService.batchUpdateTransactionStatuses(selectedWorkspaceId, transactionIds, trimmedStatus);
            showToast(`Moved ${transactionIds.length} transaction${transactionIds.length === 1 ? '' : 's'}.`);
        }, "Failed to update transactions.");
    };

    const handleReorderStatus = (statusName, direction, sectionStatusNames = []) => {
        if (!statusName || !direction) return;
        const normalizedSection = Array.isArray(sectionStatusNames)
            ? sectionStatusNames.map(name => (name || '').trim()).filter(Boolean)
            : [];
        const scopedStatuses = normalizedSection.length > 0
            ? statusOrder.filter(name => normalizedSection.includes(name))
            : statusOrder.slice();
        const scopedIndex = scopedStatuses.findIndex(name => name === statusName);
        if (scopedIndex < 0) return;
        const scopedTargetIndex = direction === 'up' ? scopedIndex - 1 : scopedIndex + 1;
        if (scopedTargetIndex < 0 || scopedTargetIndex >= scopedStatuses.length) return;

        const targetStatusName = scopedStatuses[scopedTargetIndex];
        const index = statusOrder.findIndex(name => name === statusName);
        const targetIndex = statusOrder.findIndex(name => name === targetStatusName);
        if (index < 0 || targetIndex < 0) return;

        const nextOrder = statusOrder.slice();
        [nextOrder[index], nextOrder[targetIndex]] = [nextOrder[targetIndex], nextOrder[index]];
        applyLocalStatusOrder(nextOrder);
        persistStatusOrder(nextOrder);
    };

    const handleMoveStatusBefore = (statusName, targetStatusName, sectionStatusNames = []) => {
        if (!statusName) return;
        const trimmedName = (statusName || '').trim();
        const trimmedTarget = (targetStatusName || '').trim();
        if (!trimmedName || trimmedName === trimmedTarget) return;

        const normalizedSection = Array.isArray(sectionStatusNames)
            ? sectionStatusNames.map(name => (name || '').trim()).filter(Boolean)
            : [];
        const scopedStatuses = normalizedSection.length > 0
            ? statusOrder.filter(name => normalizedSection.includes(name))
            : statusOrder.slice();
        const sectionSet = new Set(scopedStatuses);
        if (!sectionSet.has(trimmedName)) return;
        if (trimmedTarget && !sectionSet.has(trimmedTarget)) return;

        const reorderedSection = scopedStatuses.filter(name => name !== trimmedName);
        if (trimmedTarget) {
            const targetIdx = reorderedSection.indexOf(trimmedTarget);
            if (targetIdx < 0) return;
            reorderedSection.splice(targetIdx, 0, trimmedName);
        } else {
            reorderedSection.push(trimmedName);
        }

        let sectionCursor = 0;
        const nextOrder = statusOrder.map(name => {
            if (!sectionSet.has(name)) return name;
            const replacement = reorderedSection[sectionCursor];
            sectionCursor += 1;
            return replacement;
        });

        applyLocalStatusOrder(nextOrder);
        persistStatusOrder(nextOrder);
    };

    const handleSetStatusMinimized = async (statusName, shouldMinimize) => {
        if (!selectedWorkspaceId || !statusName) return;
        const trimmed = (statusName || '').trim();
        if (!trimmed) return;
        const nextMinimized = sanitizeStatusNameList(
            shouldMinimize
                ? [...(selectedWorkspace?.minimizedStatuses || []), trimmed]
                : (selectedWorkspace?.minimizedStatuses || []).filter(name => (name || '').trim().toLowerCase() !== trimmed.toLowerCase()),
            statusOrder
        );
        await run(
            () => persistStatusMetadata({ minimizedStatuses: nextMinimized }),
            "Failed to update status visibility."
        );
    };

    return {
        handleAddStatus,
        handleRenameStatus,
        handleDeleteStatus,
        handleBulkMoveTransactions,
        handleReorderStatus,
        handleMoveStatusBefore,
        handleSetStatusMinimized,
    };
}
